import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Menu, X, Phone, ChevronDown, ChevronRight, Home as HomeIcon,
  Briefcase, Plane, Camera, Sparkles, Music, Car, Wine, Bus,
  ShieldCheck, UserCheck, CalendarCheck2, Mail, Clock, MapPin,
} from "lucide-react";
import { SITE } from "../data/site";

const SERVICE_LINKS = [
  { to: "/services/airport-transportation", label: "Airport Transportation", icon: Plane },
  { to: "/services/corporate-travel", label: "Corporate Travel", icon: Briefcase },
  { to: "/services/tourism-sightseeing", label: "Tourism & Sightseeing", icon: Camera },
  { to: "/services/special-events", label: "Special Events", icon: Sparkles },
  { to: "/services/sporting-events-concerts", label: "Sporting Events & Concerts", icon: Music },
  { to: "/services/local-rides", label: "Local Rides", icon: Car },
  { to: "/services/wine-tours", label: "Wine Tours", icon: Wine },
  { to: "/services/coaches-buses", label: "Coaches & Buses", icon: Bus },
  { to: "/services/armed-security-transportation", label: "Armed Security", icon: ShieldCheck },
  { to: "/executive-chauffeur", label: "Executive Chauffeur", icon: UserCheck },
  { to: "/zuber-valet", label: "Zuber Valet Service", icon: CalendarCheck2 },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [mobileSvcOpen, setMobileSvcOpen] = useState(false);
  const loc = useLocation();

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
    setSvcOpen(false);
    setMobileSvcOpen(false);
  }, [loc.pathname]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (open) {
      const orig = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = orig; };
    }
  }, [open]);

  const linkBase = "text-[13px] tracking-[0.15em] uppercase font-medium transition-colors duration-200";
  const linkClass = ({ isActive }) =>
    `${linkBase} ${isActive ? "text-white" : "text-white/70 hover:text-white"}`;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10"
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={SITE.logo} alt="USA Limo Service" className="h-12 w-12 object-contain" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-lg text-white tracking-wide">USA Limo Service</span>
            <span className="text-[10px] tracking-[0.3em] text-white/50 uppercase">Since 1984</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          <NavLink to="/" end className={linkClass} data-testid="nav-home">Home</NavLink>
          <div
            className="relative"
            onMouseEnter={() => setSvcOpen(true)}
            onMouseLeave={() => setSvcOpen(false)}
          >
            <button
              className={`${linkBase} text-white/70 hover:text-white flex items-center gap-1`}
              data-testid="nav-services-toggle"
            >
              Services <ChevronDown size={14} />
            </button>
            {svcOpen && (
              <div className="absolute top-full left-0 pt-3 w-72" data-testid="nav-services-menu">
                <div className="bg-[#0a0a0a] border border-white/10 shadow-2xl p-2">
                  <NavLink to="/services" className="block px-4 py-2.5 text-xs uppercase tracking-widest text-white/80 hover:text-white hover:bg-white/5">All Services</NavLink>
                  <div className="h-px bg-white/10 my-1" />
                  {SERVICE_LINKS.map((s) => (
                    <NavLink
                      key={s.to}
                      to={s.to}
                      className="block px-4 py-2.5 text-xs tracking-wider text-white/70 hover:text-white hover:bg-white/5"
                    >
                      {s.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/fleet" className={linkClass} data-testid="nav-fleet">Fleet</NavLink>
          <NavLink to="/book" className={linkClass} data-testid="nav-book">Book</NavLink>
          <NavLink to="/contact" className={linkClass} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${SITE.phonesTel.virginia}`}
            className="text-white/80 hover:text-white text-[13px] flex items-center gap-2"
            data-testid="nav-call"
          >
            <Phone size={14} /> {SITE.phones.virginia}
          </a>
          <Link to="/book" className="btn-accent" data-testid="nav-book-cta">Book Online</Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden text-white p-2 z-[60] relative"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile slide-in panel + backdrop */}
      {/* Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 z-40 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      {/* Panel */}
      <aside
        className={`lg:hidden fixed top-0 right-0 h-[100dvh] w-[88%] max-w-sm bg-[#0a0a0a] border-l border-white/10 z-50 transform transition-transform duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] flex flex-col ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="nav-mobile-menu"
        role="dialog"
        aria-label="Main menu"
      >
        {/* Panel header */}
        <div className="px-6 pt-6 pb-5 border-b border-white/10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" onClick={() => setOpen(false)}>
            <img src={SITE.logo} alt="USA Limo" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display text-base text-white leading-tight">USA Limo Service</div>
              <div className="text-[9px] tracking-[0.3em] text-[#d4af6a] uppercase">Since 1984</div>
            </div>
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="text-white/70 hover:text-white p-2 -mr-2"
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        {/* Scrollable menu body */}
        <nav className="flex-1 overflow-y-auto overscroll-contain px-3 py-5">
          <SectionLabel>Menu</SectionLabel>
          <MobileLink to="/" icon={HomeIcon} onClose={() => setOpen(false)}>Home</MobileLink>
          <MobileLink to="/fleet" icon={Car} onClose={() => setOpen(false)}>Our Fleet</MobileLink>
          <MobileLink to="/book" icon={CalendarCheck2} onClose={() => setOpen(false)}>Book Your Ride</MobileLink>
          <MobileLink to="/contact" icon={Mail} onClose={() => setOpen(false)}>Contact</MobileLink>

          <SectionLabel>Services</SectionLabel>

          {/* All services accordion */}
          <button
            onClick={() => setMobileSvcOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-3 py-3.5 text-white/85 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
            data-testid="mobile-svc-toggle"
            aria-expanded={mobileSvcOpen}
          >
            <span className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center bg-white/[0.03] border border-white/10">
                <Briefcase size={15} className="text-[#d4af6a]" />
              </span>
              <span className="text-[15px] tracking-wide">All Services</span>
            </span>
            <ChevronRight size={16} className={`text-white/40 transition-transform duration-300 ${mobileSvcOpen ? "rotate-90" : ""}`} />
          </button>

          {/* Services accordion content */}
          <div
            className={`overflow-hidden transition-[max-height,opacity] duration-500 ${mobileSvcOpen ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"}`}
          >
            <div className="pl-3 ml-3 border-l border-white/10 mt-1 mb-2">
              <NavLink
                to="/services"
                onClick={() => setOpen(false)}
                className="block px-3 py-2.5 text-[12px] tracking-[0.2em] uppercase text-[#d4af6a] hover:text-white"
              >
                View All →
              </NavLink>
              {SERVICE_LINKS.map((s) => (
                <NavLink
                  key={s.to}
                  to={s.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-sm ${
                      isActive ? "text-white bg-white/5" : "text-white/65 hover:text-white hover:bg-white/[0.03]"
                    }`
                  }
                  data-testid={`mobile-svc-${s.to.split("/").pop()}`}
                >
                  <s.icon size={14} className="text-white/50 flex-shrink-0" />
                  <span>{s.label}</span>
                </NavLink>
              ))}
            </div>
          </div>

          <SectionLabel>Contact</SectionLabel>
          <a
            href={`tel:${SITE.phonesTel.office}`}
            className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-white/5 rounded-sm"
          >
            <span className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center bg-white/[0.03] border border-white/10">
                <Phone size={14} className="text-[#d4af6a]" />
              </span>
              <span>
                <span className="block text-[10px] tracking-[0.3em] uppercase text-white/45">Office</span>
                <span className="block text-white text-sm">{SITE.phones.office}</span>
              </span>
            </span>
          </a>
          <a
            href={`tel:${SITE.phonesTel.virginia}`}
            className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-white/5 rounded-sm"
          >
            <span className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center bg-white/[0.03] border border-white/10">
                <Phone size={14} className="text-[#d4af6a]" />
              </span>
              <span>
                <span className="block text-[10px] tracking-[0.3em] uppercase text-white/45">Virginia</span>
                <span className="block text-white text-sm">{SITE.phones.virginia}</span>
              </span>
            </span>
          </a>
          <a
            href={`tel:${SITE.phonesTel.dispatch}`}
            className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-white/5 rounded-sm"
          >
            <span className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center bg-[#9B1B30]/15 border border-[#9B1B30]/40">
                <Clock size={14} className="text-[#9B1B30]" />
              </span>
              <span>
                <span className="block text-[10px] tracking-[0.3em] uppercase text-[#9B1B30]/80">Dispatch · 24/7</span>
                <span className="block text-white text-sm">{SITE.phones.dispatch}</span>
              </span>
            </span>
          </a>
          <div className="flex items-center gap-3 px-3 py-3 text-white/55 text-xs">
            <MapPin size={14} className="text-white/40" />
            {SITE.address}
          </div>
        </nav>

        {/* Sticky bottom CTA bar */}
        <div className="border-t border-white/10 bg-[#050505] px-4 py-4 grid grid-cols-2 gap-2.5">
          <a
            href={`tel:${SITE.phonesTel.dispatch}`}
            className="btn-outline justify-center text-xs"
            data-testid="mobile-cta-call"
          >
            <Phone size={14} /> Call Now
          </a>
          <Link
            to="/book"
            onClick={() => setOpen(false)}
            className="btn-accent justify-center text-xs"
            data-testid="mobile-cta-book"
          >
            Book Online
          </Link>
        </div>
      </aside>
    </header>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="px-3 mt-5 mb-2 text-[10px] tracking-[0.4em] uppercase text-white/35 font-medium">
      {children}
    </div>
  );
}

function MobileLink({ to, icon: Icon, children, onClose }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      onClick={onClose}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-3.5 rounded-sm transition-colors ${
          isActive ? "bg-white/5 text-white" : "text-white/85 hover:text-white hover:bg-white/5"
        }`
      }
    >
      <span className="w-9 h-9 flex items-center justify-center bg-white/[0.03] border border-white/10">
        <Icon size={15} className="text-[#d4af6a]" />
      </span>
      <span className="text-[15px] tracking-wide">{children}</span>
    </NavLink>
  );
}
